import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Loading from 'components/Loading';
import ProviderItem from 'components/search/ProviderItem';
import PoweredBy from 'components/PoweredBy';
import { Spacing, Text } from '@reservamos/elements';
import useFinalPriceExperiment from 'hooks/useFinalPriceExperiment';
import NoResults from '../../ui/molecules/NoResults';
import ResultLine from './ResultLine';
import NewResultLine from './NewResultLine';
import LabelLoading from '../../ui/molecules/LabelLoading';

/**
 * The price a provider shows and sorts by in control: its base minimum.
 *
 * @param {object} provider - A provider as `setProviders` stores it.
 * @returns {number} The base minimum.
 */
const baseLowestPrice = (provider) => provider.lowestPrice;

/**
 * The price a provider shows and sorts by in the BOOK-168 final arm: the final minimum
 * `setProviders` recorded, or the base one for a provider without companions, a flight or a mixed
 * itinerary.
 *
 * @param {object} provider - A provider as `setProviders` stores it.
 * @returns {number} The final minimum, or the base one.
 */
const finalLowestPrice = (provider) => provider.lowestFinalPrice ?? provider.lowestPrice;

/**
 * Whether any provider would show or sort differently in the final arm: the gate on evaluating
 * the experiment, so that a list without markup never logs an exposure. The return leg paints the
 * round values but still sorts on the one-way ones, so both count there.
 *
 * @param {Array} providers - The providers the list can show.
 * @param {string} way - `departure` or `return`.
 * @returns {boolean} Whether a final value sits above its base.
 */
const hasFinalMarkup = (providers, way) =>
  providers.some(
    (provider) =>
      provider.lowestFinalPrice > provider.lowestPrice ||
      (way === 'return' && provider.roundLowestFinalPrice > provider.roundLowestPrice),
  );

/**
 * The order without a chosen sort: by importance when both lines have one, else by price.
 *
 * @param {object} a - A provider.
 * @param {object} b - Another provider.
 * @param {Function} priceOf - The price of a provider in the user's arm.
 * @returns {number} The comparison result.
 */
const defaultSort = (a, b, priceOf) => {
  if (a.importance !== undefined && b.importance !== undefined) {
    return a.importance - b.importance;
  }
  return priceOf(a) - priceOf(b);
};

/**
 * The order for the chosen sort. A price sort (`price` or `lowestPrice`) uses the price the user's
 * arm paints, so the list never orders by one figure while it shows another.
 *
 * @param {object} a - A provider.
 * @param {object} b - Another provider.
 * @param {string} property - The sort: `price`, `lowestPrice`, `duration` or `tripCount`.
 * @param {Function} priceOf - The price of a provider in the user's arm.
 * @returns {number} The comparison result.
 */
const sortProvidersBy = (a, b, property, priceOf) => {
  const propertyToUse = property === 'price' ? 'lowestPrice' : property;

  if (propertyToUse === 'lowestPrice') {
    return priceOf(a) - priceOf(b);
  }

  if (propertyToUse === 'tripCount') {
    return b[propertyToUse] - a[propertyToUse];
  }

  return a[propertyToUse] - b[propertyToUse];
};

const filterProvidersBy = (providers, property) => {
  const propertyToUse = property && Array.isArray(property) ? property : [property];
  if (!propertyToUse || propertyToUse[0] === 'none') {
    return providers;
  }
  return providers.filter((provider) => {
    let condition = false;
    propertyToUse.forEach((prop) => {
      condition = condition || provider.departures.includes(prop);
    });
    return condition;
  });
};

function departuresDescription(departures, t) {
  if (departures.length === 4) return t('hours', { context: 'all' });
  const sorted = [];

  // crappy sorting
  if (departures.includes('earlymorning')) sorted.push('earlymorning');
  if (departures.includes('morning')) sorted.push('morning');
  if (departures.includes('afternoon')) sorted.push('afternoon');
  if (departures.includes('night')) sorted.push('night');

  return sorted
    .map((departure) => t('hours', { context: departure }))
    .join(', ')
    .replace(/,(?!.*,)/, t('hours_separator'));
}

/**
 * ProviderList component. Render the providers screen's lines, filtered and sorted.
 *
 * @param {object} props - Component props.
 * @param {boolean} props.roundTrip - Whether the search is a round trip.
 * @param {string} props.way - `departure` or `return`.
 * @param {Array} props.providers - The providers, as `setProviders` stores them.
 * @param {string} props.sortBy - The chosen sort.
 * @param {string|Array} props.filterBy - The departure-time filters.
 * @param {number} props.installmentsMinAmount - Minimum amount for installments.
 * @param {Function} props.onSelectProvider - Called with the picked provider.
 * @param {Function} props.onDisclaimerClick - Called when the disclaimer is clicked.
 * @param {boolean} props.isLoading - Whether the search is still polling.
 * @param {boolean} props.isOpenTicket - Whether the list is for open tickets.
 * @param {boolean} props.isNotFiltered - Whether to list the providers the filters leave out.
 * @param {string} props.date - The search date.
 * @param {boolean} props.showPoweredBy - Whether to show the powered-by footer.
 * @returns {JSX.Element|null} The rendered list.
 */
const ProviderList = ({
  roundTrip,
  way = 'departure',
  providers,
  sortBy,
  filterBy,
  installmentsMinAmount,
  onSelectProvider,
  onDisclaimerClick,
  isLoading,
  isOpenTicket,
  isNotFiltered,
  date,
  showPoweredBy,
}) => {
  const { features } = useSelector((state) => state.whitelabelConfig);
  const { t } = useTranslation('search');
  const isNewResultsDesignAB = features.NEW_RESULTS_DESIGN;
  // Resolved once for the whole list, at render, so providers parsed before GrowthBook loaded show
  // whatever arm the tab resolves; both the painted price and the sort follow it.
  const showFinalPrice = useFinalPriceExperiment({ hasMarkup: hasFinalMarkup(providers, way) });
  const priceOf = showFinalPrice ? finalLowestPrice : baseLowestPrice;

  if (providers.length < 1 && !isLoading && !isNotFiltered) {
    return <NoResults way={way} isProviderList date={date} />;
  }

  if (providers.length < 1 && !isNotFiltered) {
    return <Loading hasHeader={false} loadingText={t('loading_results')} />;
  }

  let notFilteredProviders;
  const filteredProviders = filterProvidersBy(providers, filterBy);

  if (isNotFiltered) {
    const filteredProvidersMap = filteredProviders.map((provider) => provider.id);
    notFilteredProviders = providers.filter(
      (provider) => !filteredProvidersMap.includes(provider.id),
    );
  }

  const providersToSort = isNotFiltered ? notFilteredProviders : filteredProviders;
  const orderedProviders = providersToSort.sort((a, b) => {
    return sortBy ? sortProvidersBy(a, b, sortBy, priceOf) : defaultSort(a, b, priceOf);
  });

  // The return leg sorts on the one-way minimum above and paints the round values, in both arms.
  let providersToShow = orderedProviders;
  if (way === 'return') {
    providersToShow = orderedProviders.map((provider) => {
      const { roundProviderDiscount, roundLowestPrice, roundLowestFinalPrice, roundHasDiscounts } =
        provider;
      return {
        ...provider,
        providerDiscount: roundProviderDiscount,
        lowestPrice: roundLowestPrice,
        lowestFinalPrice: roundLowestFinalPrice,
        hasDiscounts: roundHasDiscounts,
      };
    });
  }

  const isNotFilteredList = notFilteredProviders?.length && isNotFiltered;

  if (isNotFiltered && !notFilteredProviders?.length) return null;

  return (
    <>
      {isLoading && <LabelLoading />}
      <Spacing vertical>
        {isNotFilteredList ? (
          <Text weight="bold" size="L" mobileSize="M">
            {t('label.other_lines_you_may_be_interested_on')}
          </Text>
        ) : null}
        <div className="trip-container fade-in">
          <Spacing size="S" vertical>
            {providersToShow.map((provider, index) => {
              const { departures } = provider;
              const price = priceOf(provider);
              if (isNewResultsDesignAB) {
                return (
                  <NewResultLine
                    onSelectClick={() => onSelectProvider(Object.assign(provider, { index }))}
                    provider={provider}
                    price={price}
                    departuresDescription={departuresDescription(departures, t)}
                  />
                );
              }
              if (features.NEW_PROVIDER_RESULTS) {
                return (
                  <ResultLine
                    onSelectClick={() => onSelectProvider(Object.assign(provider, { index }))}
                    provider={provider}
                    price={price}
                    departuresDescription={departuresDescription(departures, t)}
                    isOpenTicket={isOpenTicket}
                  />
                );
              }
              return (
                <ProviderItem
                  key={provider.id}
                  provider={provider}
                  price={price}
                  roundTrip={roundTrip}
                  way={way}
                  position={index + 1}
                  installmentsMinAmount={installmentsMinAmount}
                  onSelectProvider={() => onSelectProvider(Object.assign(provider, { index }))}
                  onDisclaimerClick={onDisclaimerClick}
                  departuresDescription={departuresDescription(departures, t)}
                />
              );
            })}
          </Spacing>

          {features.SHOW_POWERED_BY && showPoweredBy && <PoweredBy />}
        </div>
      </Spacing>
    </>
  );
};

ProviderList.propTypes = {
  providers: PropTypes.array.isRequired,
  roundTrip: PropTypes.bool.isRequired,
  sortBy: PropTypes.string,
  filterBy: PropTypes.string,
  way: PropTypes.string,
  installmentsMinAmount: PropTypes.number.isRequired,
  onSelectProvider: PropTypes.func.isRequired,
  onDisclaimerClick: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isOpenTicket: PropTypes.bool,
  isNotFiltered: PropTypes.bool,
  date: PropTypes.string,
  showPoweredBy: PropTypes.bool,
};

ProviderList.defaultProps = {
  isOpenTicket: false,
};

export default ProviderList;
